import React, { useEffect, useMemo, useState } from 'react';
import { FaSave, FaSpinner, FaTimes } from 'react-icons/fa';
import { SearchableSelect } from '../../utils/select-with-search';
import { Department, DepartmentHoliday, DepartmentProject, EditDepartmentFormData } from './types';
import { HolidayMultiSelect } from './holiday_select';

interface EditDepartmentOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EditDepartmentFormData) => void;
  department: Department | null;
  loading?: boolean;
  projects: DepartmentProject[];
  projectsLoading?: boolean;
  holidays: DepartmentHoliday[];
  holidaysLoading?: boolean;
}

export const EditDepartmentOffcanvas: React.FC<EditDepartmentOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  department,
  loading = false,
  projects,
  projectsLoading = false,
  holidays,
  holidaysLoading = false
}) => {
  const [name, setName] = useState('');
  const [holidayIds, setHolidayIds] = useState<number[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<{ name?: string; project_ids?: string }>({});

  const projectOptions = useMemo(() => {
    const projectMap = new Map<number, DepartmentProject>();
    projects.forEach((project) => projectMap.set(project.id, project));
    department?.projects?.forEach((project) => projectMap.set(project.id, project));

    return Array.from(projectMap.values()).map((project) => ({
      value: project.id,
      label: project.project_short_name
        ? `${project.project_name} (${project.project_short_name})`
        : project.project_name
    }));
  }, [department?.projects, projects]);

  useEffect(() => {
    if (isOpen && department) {
      setName(department.name || '');
      const selectedProjectOptions = (department.project_ids || []).map((projectId) => {
        const matchedProject = projectOptions.find((option) => Number(option.value) === projectId);
        return matchedProject || {
          value: projectId,
          label: `Project ID: ${projectId}`
        };
      });
      setProjectIds(department.project_ids || []);
      setHolidayIds(department.holiday_ids || []);
      setSelectedProjects(selectedProjectOptions);
      setErrors({});
    }
  }, [isOpen, department, projectOptions]);

  const validate = () => {
    const trimmed = name.trim();
    const nextErrors: { name?: string; project_ids?: string } = {};
    if (!trimmed) {
      nextErrors.name = 'Department name is required';
    }
    if (trimmed && trimmed.length < 2) {
      nextErrors.name = 'Department name must be at least 2 characters';
    }
    if (trimmed.length > 120) {
      nextErrors.name = 'Department name cannot exceed 120 characters';
    }
    if (projectIds.some((projectId) => !Number.isFinite(projectId))) {
      nextErrors.project_ids = 'Please select valid projects';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!department || !validate()) return;
    onSubmit({ id: department.id, name: name.trim(), project_ids: projectIds, holiday_ids: holidayIds });
  };

  if (!isOpen || !department) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Department</h2>
            <p className="text-sm text-gray-600 mt-1">Update department details</p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
              <div>
                <span className="block text-gray-500">Department ID</span>
                <span className="font-medium text-gray-800">{department.id}</span>
              </div>
              <div>
                <span className="block text-gray-500">Updated</span>
                <span className="font-medium text-gray-800">{new Date(department.updated_at).toLocaleString()}</span>
              </div>
            </div>

            <div>
              <label htmlFor="department-name-edit" className="block text-sm font-medium text-gray-700 mb-2">
                Department Name *
              </label>
              <input
                id="department-name-edit"
                type="text"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                disabled={loading}
                placeholder="Enter department name"
                maxLength={120}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projects
              </label>
              <SearchableSelect
                value={selectedProjects}
                onChange={(options: any) => {
                  const normalizedOptions = Array.isArray(options) ? options : [];
                  setSelectedProjects(normalizedOptions);
                  setProjectIds(
                    normalizedOptions
                      .map((option) => Number(option.value))
                      .filter((value) => Number.isFinite(value))
                  );
                  if (errors.project_ids) setErrors((prev) => ({ ...prev, project_ids: undefined }));
                }}
                options={projectOptions}
                placeholder="Search and select projects"
                disabled={loading}
                isLoading={projectsLoading}
                isMulti={true}
                showSelectAllVisible={true}
                error={errors.project_ids}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Holidays</label>
              <HolidayMultiSelect
                holidays={holidays}
                linked={department.holidays || []}
                value={holidayIds}
                onChange={setHolidayIds}
                loading={holidaysLoading}
                disabled={loading}
              />
            </div>
          </form>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white rounded transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Update Department
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

